import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/utils/cn'

export interface DropdownPortalProps {
  children: React.ReactNode
  isOpen: boolean
  targetRef: React.RefObject<HTMLElement | null>
  className?: string
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  offset?: number
}

export const DropdownPortal: React.FC<DropdownPortalProps> = ({
  children,
  isOpen,
  targetRef,
  className,
  placement = 'bottom-start',
  offset = 4
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen || !targetRef.current) return

    const calculatePosition = () => {
      const targetRect = targetRef.current!.getBoundingClientRect()
      const dropdownRect = dropdownRef.current?.getBoundingClientRect()

      let top = 0
      let left = 0

      // Calculate vertical position
      if (placement.startsWith('bottom')) {
        top = targetRect.bottom + offset
      } else {
        top = targetRect.top - (dropdownRect?.height || 0) - offset
      }

      // Calculate horizontal position
      if (placement.endsWith('start')) {
        left = targetRect.left
      } else {
        left = targetRect.right - (dropdownRect?.width || 0)
      }

      // Ensure dropdown stays within viewport
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      // Adjust horizontal position if needed
      if (left + (dropdownRect?.width || 0) > viewportWidth) {
        left = viewportWidth - (dropdownRect?.width || 0) - 10
      }
      if (left < 10) {
        left = 10
      }

      // Adjust vertical position if needed
      if (top + (dropdownRect?.height || 0) > viewportHeight) {
        // Flip to top if bottom doesn't fit
        if (placement.startsWith('bottom')) {
          top = targetRect.top - (dropdownRect?.height || 0) - offset
        }
      }

      setPosition({ top, left })
    }

    calculatePosition()

    // Recalculate on scroll or resize
    const handleScroll = () => calculatePosition()
    const handleResize = () => calculatePosition()

    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [isOpen, targetRef, placement, offset])

  if (!isOpen) return null

  return createPortal(
    <div
      ref={dropdownRef}
      className={cn('fixed z-dropdown', className)}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
    >
      {children}
    </div>,
    document.body
  )
}

export default DropdownPortal
